import BottomNavUtils from '@/modules/layout/bottomNav/utils/bottomNavUtils';
import bottomNavItems from '@/modules/layout/bottomNav/bottomNavItems';
import LayoutConstants from '@/constansts/layoutConstants';
import {
  buildMobilePlatformBottomTabs,
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES,
} from '@/modules/layout/platformNavigation/utils/mobilePlatformBottomTabs';
import {
  buildPathSet,
  filterBottomNavTabsByPathSet,
  getBottomNavTabPaths,
  normalizeMobileNavigationPath,
} from '@/modules/layout/platformNavigation/utils/mobileNavigationDuplicateFiltering';

const MOBILE_GLASSY_NAV_FLAG = 'rollout.platform_administration.mobile_glassy_nav_layout';
const MOBILE_PLATFORM_NAV_FLAG = 'rollout.platform_administration.mobile_platform_nav';

const isMobilePlatformNavigationActive = (rootGetters) => {
  const hasFeature = rootGetters['BusinessStore/hasFeature'];
  return hasFeature?.(MOBILE_GLASSY_NAV_FLAG) && hasFeature?.(MOBILE_PLATFORM_NAV_FLAG);
};

const hasInboxTab = (tabs) => tabs.some((tab) => tab.name === 'inbox');
const hasPulseTab = (tabs) => tabs.some((tab) => tab.name === 'pulse');
const ACTION_ONLY_MOBILE_PLATFORM_TAB_TYPES = [
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_MENU,
  MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_GRID,
];

const isActionOnlyMobilePlatformTab = (tab) => ACTION_ONLY_MOBILE_PLATFORM_TAB_TYPES.includes(tab?.actionType);

// Action-only tabs open another mobile navigation surface instead of representing the current route.
// Keeping them selected blocks tapping them again, so clear the selection after the action runs.
const shouldClearCurrentIndex = (tabs, currentIndex) => {
  const bottomNavTabs = tabs || [];
  if (!Number.isInteger(currentIndex) || currentIndex < 0 || bottomNavTabs.length === 0) {
    return false;
  }

  const currentTab = bottomNavTabs[currentIndex];
  return !currentTab || isActionOnlyMobilePlatformTab(currentTab);
};

// Menu tabs only open the main menu, so they must not stay selected once it closes.
// Picking the page already displayed fires no route/state change to move the selection,
// and VcBottomNav (`optional` v-tabs) turns the next tap on a selected tab into a deselect.
const isMenuOpeningTab = (tab) => (
  (tab?.name === 'menu' && tab?.actionType === LayoutConstants.BOTTOM_NAV_ACTION_TYPES.POV_ACTION)
  || tab?.actionType === MOBILE_PLATFORM_BOTTOM_ACTION_TYPES.MODULE_MENU
);

const getCurrentRouteTabIndex = (tabs, currentRoutePath) => {
  const normalizedRoutePath = normalizeMobileNavigationPath(currentRoutePath);
  if (!normalizedRoutePath) return -1;

  return (tabs || []).findIndex((tab) => getBottomNavTabPaths(tab).includes(normalizedRoutePath));
};

const state = {
  bottomNavConfig: {
    show: true,
    bottomNavTabs: bottomNavItems,
    features: [],
    permissions: [],
    currentIndex: undefined,
    indexBeforeMenu: undefined,
  },
  isLoading: true,
};

const mutations = {
  setShow(state, show) {
    state.bottomNavConfig.show = show;
  },
  setBottomNavTabs(state, tabs) {
    state.bottomNavConfig.bottomNavTabs = tabs;
    state.isLoading = false;
  },
  setCurrentIndex(state, newIndex) {
    const { bottomNavTabs, currentIndex } = state.bottomNavConfig;
    if (isMenuOpeningTab(bottomNavTabs?.[newIndex]) && !isMenuOpeningTab(bottomNavTabs?.[currentIndex])) {
      state.bottomNavConfig.indexBeforeMenu = currentIndex;
    }
    if (newIndex < 0) {
      state.bottomNavConfig.currentIndex = undefined;
    } else {
      state.bottomNavConfig.currentIndex = newIndex;
    }
  },
  setInboxTabConversationCounts(state, counter) {
    const inboxTabIndex = state.bottomNavConfig.bottomNavTabs.findIndex((tab) => tab.name === 'inbox');
    if (inboxTabIndex < 0) {
      return;
    }
    state.bottomNavConfig.bottomNavTabs[inboxTabIndex].badgeValue = counter;
  },
  setPulseTabBadgeCount(state, counter) {
    const pulseTabIndex = state.bottomNavConfig.bottomNavTabs.findIndex((tab) => tab.name === 'pulse');
    if (pulseTabIndex < 0) {
      return;
    }
    state.bottomNavConfig.bottomNavTabs[pulseTabIndex].badgeValue = counter;
  },
};

const getters = {
  bottomNavTabs: (state) => state.bottomNavConfig.bottomNavTabs,
  showBottomNav: (state) => state.bottomNavConfig.show,
  currentIndex: (state) => state.bottomNavConfig.currentIndex,
  isLoading: (state) => state.isLoading,
};

const actions = {
  async createBottomNavTabs({ rootGetters, commit, state: storeState = state }) {
    const fallbackTabs = BottomNavUtils.getAggregatedTabs(rootGetters);
    const activeItem = rootGetters['PlatformNavigationStore/activeTopBarItem'];
    const isMobilePlatformActive = isMobilePlatformNavigationActive(rootGetters);
    const filteredFallbackTabs = isMobilePlatformActive && activeItem?.isBMP
      ? filterBottomNavTabsByPathSet(
        fallbackTabs,
        buildPathSet(rootGetters['PlatformNavigationStore/nonBmpNavigationPaths']),
      )
      : fallbackTabs;
    const tabs = buildMobilePlatformBottomTabs({
      activeItem,
      fallbackTabs: filteredFallbackTabs,
      getItemTitle: rootGetters['PlatformNavigationStore/getItemTitle'],
      isMobilePlatformNavigationActive: isMobilePlatformActive,
      hasMultipleModules: rootGetters['PlatformNavigationStore/hasMultipleMobileModules'],
    });
    commit('setBottomNavTabs', tabs);
    const currentRouteIndex = getCurrentRouteTabIndex(
      tabs,
      rootGetters['PlatformNavigationStore/currentRoutePath'],
    );
    if (currentRouteIndex >= 0) {
      commit('setCurrentIndex', currentRouteIndex);
    } else if (shouldClearCurrentIndex(tabs, storeState.bottomNavConfig.currentIndex)) {
      commit('setCurrentIndex', -1);
    }
  },
  setBottomNavConfig({ commit }, bottomNavConfig) {
    commit('setShow', bottomNavConfig.show);
  },
  setBottomNavStateChange({ commit }, stateName) {
    const index = BottomNavUtils.getTabIndexForAction(state, stateName);
    commit('setCurrentIndex', index);
  },
  setCurrentIndex({ commit, state: storeState = state }, index) {
    const tabs = storeState.bottomNavConfig.bottomNavTabs;
    commit('setCurrentIndex', shouldClearCurrentIndex(tabs, index) ? -1 : index);
  },
  restoreIndexAfterMenuClose({ commit, state: storeState = state }) {
    const { bottomNavTabs, currentIndex, indexBeforeMenu } = storeState.bottomNavConfig;
    if (!isMenuOpeningTab(bottomNavTabs?.[currentIndex])) return;

    commit('setCurrentIndex', indexBeforeMenu ?? -1);
  },
  async updateInboxTabConversationCounts({ state, rootGetters, dispatch, commit }) {
    if (!hasInboxTab(state.bottomNavConfig.bottomNavTabs)) {
      return;
    }

    await dispatch({ type: 'ConversationStore/fetchConversationCounts' }, { root: true });
    if (hasInboxTab(state.bottomNavConfig.bottomNavTabs)) {
      const counter = rootGetters['ConversationStore/counts'];
      commit('setInboxTabConversationCounts', counter);
    }
  },
  // The fetch commits the tab's badge count itself (pulseStore's syncPulseBadge).
  async updatePulseTabNeedsAttentionCount({ state, dispatch }) {
    if (!hasPulseTab(state.bottomNavConfig.bottomNavTabs)) {
      return;
    }

    await dispatch({ type: 'PulseStore/fetchNeedsAttentionCount' }, { root: true });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
